import { Box, Button, TextField, Typography } from '@mui/material';
import { DataGridPro, GridColDef, GridRenderCellParams, GridValueGetterParams } from '@mui/x-data-grid-pro';
import React, { useCallback, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { NavLink } from 'react-router-dom';

import { If } from 'app/components/if';
import { RouterLink } from 'app/components/router-link';
import { GeneralConstants } from 'app/constants/general-constants';
import { useCallbackOnProdSwitch } from 'app/hooks';
import { Brand } from 'app/model/cris/brand';
import { searchService } from 'app/services/search-service';

import { selectProdDataStatus } from '../cris.slice';

const columns: Array<GridColDef<Brand>> = [
    {
        field: 'id',
        headerName: 'ID',
        width: 150,
    },
    {
        field: 'name',
        headerName: 'Name',
        width: 250,
        renderCell: (data: GridRenderCellParams<Brand, Brand['name']>) => (
            <RouterLink to={`/cris/brands/${data.row.id}/details`}>{data.value}</RouterLink>
        ),
    },
    {
        field: 'category',
        headerName: 'Category',
        width: 150,
        valueGetter: (params: GridValueGetterParams<Brand, Brand['category']>) => params.value?.category,
    },
    {
        field: 'midCount',
        headerName: 'Mid Count',
        width: 150,
    },
    {
        field: 'tagSource',
        headerName: 'Tagging Source',
        width: 150,
    },
];

const BrandSearch: React.FC = () => {
    const [brands, setBrands] = useState<Brand[]>([]);
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    const showProd = useSelector(selectProdDataStatus);
    const queryRef = useRef<HTMLInputElement>();

    const findBrands = useCallback(() => {
        if (!queryRef.current) {
            return;
        }
        searchService
            .search(queryRef.current.value, 1, GeneralConstants.PAGE_SIZE_HUGE)
            .subscribe((pageData) => setBrands(pageData.data));
    }, []);

    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    useCallbackOnProdSwitch(findBrands);

    return (
        <React.Fragment>
            <Typography variant='body1' className='mb-1'>
                Search for CRIS Brands within the system. Search field accepts brand or store name.
            </Typography>
            <form
                onSubmit={(evt) => {
                    evt.preventDefault();
                }}
            >
                <Box display='flex' mb={1}>
                    <Box flexGrow={1} mr={1}>
                        <TextField
                            name='term'
                            label='Search Term'
                            variant='outlined'
                            size='small'
                            inputRef={queryRef}
                            fullWidth
                        />
                    </Box>
                    <Button variant='contained' color='primary' type='submit' onClick={findBrands}>
                        Search
                    </Button>
                    <If condition={!showProd}>
                        <Button
                            type='button'
                            variant='outlined'
                            color='warning'
                            className='ml-1'
                            component={NavLink}
                            to='/cris/brands/new'
                        >
                            Create Brand
                        </Button>
                    </If>
                </Box>
            </form>
            <Box sx={{ width: '100%', display: 'grid', height: '100%' }}>
                <DataGridPro
                    rows={brands}
                    columns={columns}
                    paginationModel={{ pageSize: 50, page: 0 }}
                    disableRowSelectionOnClick
                    density='compact'
                    pagination
                />
            </Box>
        </React.Fragment>
    );
};

export { BrandSearch };
